import i18n from '@/i18n'
import type { ListQuery } from './index'
import type { ActivityLog, Client, Documentable } from './clients'
import type { InvoiceStatus, QuoteStatus } from './billing'
import type { PaymentMethod, PaymentStatus } from './payments'
import type { Technician } from './technicians'

export type JobStatus =
  | 'to_plan'
  | 'planned'
  | 'assigned'
  | 'in_progress'
  | 'completed'
  | 'cancelled'

// Job status values as constants so comparisons never hardcode string literals.
export const JOB_STATUS = {
  to_plan: 'to_plan',
  planned: 'planned',
  assigned: 'assigned',
  in_progress: 'in_progress',
  completed: 'completed',
  cancelled: 'cancelled',
} as const
export type JobPriority = 'low' | 'medium' | 'high' | 'urgent'
export type InterventionType =
  | 'visit_quote'
  | 'one_time_treatment'
  | 'under_contract'
  | 'warranty_return'

// Intervention type values as constants so comparisons never hardcode string literals.
export const INTERVENTION_TYPE = {
  visit_quote: 'visit_quote',
  one_time_treatment: 'one_time_treatment',
  under_contract: 'under_contract',
  warranty_return: 'warranty_return',
} as const
export type PaymentOption =
  | 'to_collect'
  | 'quote_on_site'
  | 'no_collection'
  | 'effort_based'
  | null

export interface Job {
  id: string
  tenant_id: string
  // Non-blocking warnings surfaced on create.
  payment_warning?: string | null
  scheduling_warning?: string | null
  reference: string | null
  client_id: string
  contract_id: string | null
  quote_id: string | null
  original_job_id: string | null
  priority: JobPriority
  service_type: string | null
  intervention_type: InterventionType | null
  scheduled_date: string | null
  callback_date: string | null
  callback_time: string | null
  start_time: string | null
  end_time: string | null
  country: string | null
  city: string | null
  postal_code: string | null
  address: string | null
  address_additional: string | null
  access_code: string | null
  floor: string | null
  access_information: string | null
  latitude: number | null
  longitude: number | null
  note: string | null
  status: JobStatus
  payment_option: PaymentOption
  amount_to_collect: number | null
  payment_collection_required: boolean
  no_collection_reason: string | null
  repeat_schedule_id: string | null
  cancelled_at: string | null
  cancel_reason: string | null
  started_at: string | null
  closed_at: string | null
  created_at: string
  updated_at: string
  // Derived display helper returned by the list/agenda endpoints (flat).
  client_name?: string | null
  // Optional eager relations the list endpoint may include.
  client?: Client | null
  assigned_technicians?: Technician[] | null
}

export interface JobStatusChange {
  job_id: string
  status: JobStatus
  closed_at: string | null
  updated_at: string
}

export interface JobChangesResponse {
  changes: JobStatusChange[]
  next_cursor: string
}

export interface JobDetail extends Job {
  client: Client
  assigned_technicians: Technician[]
  lead_technician_id: string | null
  attachments: Documentable[]
  report_id: string | null
  // Quote explicitly selected as a reference at job creation.
  quote_reference?: JobQuoteSummary | null
  original_job?: OriginalJobSummary | null
  // Chase-invoice summary (feedback #17). Present only when the job has a
  // linked invoice; balance_due = total - amount_paid.
  invoice?: JobInvoiceSummary | null
  // 2.3.18 - post-inspection quotes raised from this job, newest first.
  quotes?: JobQuoteSummary[] | null
  // 2.3.19 / BR11 - payments recorded against this job, newest first.
  payments?: JobPaymentSummary[] | null
  status_history?: ActivityLog[] | null
}

/** Linked post-inspection quote summary surfaced on the job detail (2.3.18). */
export interface JobQuoteSummary {
  id: string
  number: string | null
  status: QuoteStatus
  total: number
  issue_date: string | null
}

/** Linked payment summary surfaced read-only on the job detail (2.3.19). */
export interface JobPaymentSummary {
  id: string
  reference: string | null
  method: PaymentMethod
  status: PaymentStatus
  amount: number
  paid_date: string | null
}

/** Chase-invoice summary surfaced on the job detail (feedback #17). */
export interface JobInvoiceSummary {
  id: string
  number: string | null
  status: InvoiceStatus
  total: number
  amount_paid: number
  balance_due: number
  expected_payment_method: string | null
  due_date: string | null
}

export interface OriginalJobSummary {
  id: string
  reference: string | null
  service_type: string | null
  scheduled_date: string
  invoice: Pick<
    JobInvoiceSummary,
    'id' | 'number' | 'status' | 'total' | 'amount_paid' | 'balance_due'
  > | null
}

/**
 * Ranked technician suggestion for assignment (feedback #14). Score is the
 * weighted suitability (0..100); factors expose the per-criterion breakdown.
 */
export interface TechnicianSuggestion {
  technician: Technician
  score: number
  factors: {
    skills_match: boolean
    matched_skill: string | null
    available: boolean
    on_leave: boolean
    busy: boolean
    distance_km: number | null
    travel_minutes: number | null
    within_radius: boolean | null
    role_eligible: boolean
    role_requirement: 'commercial' | 'operational'
  }
}

export interface TechnicianSuggestionQuery {
  quote_id?: string
  contract_id?: string
  service_type?: string
  intervention_type?: InterventionType
  scheduled_date?: string
  start_time?: string
  end_time?: string
  latitude?: number
  longitude?: number
  priority?: JobPriority
  limit?: number
  // Name filter for the "all technicians" full search (2.3.5); when set, the
  // ranked set is restricted to technicians whose name matches.
  search?: string
  // Id filter used to hydrate the computed factors (Status / travel / proximity)
  // of already-assigned technicians in the assign/reassign UI; restricts the
  // ranked set to these ids so their factors can be shown instead of "--".
  technician_ids?: string[]
  // Job being reassigned/edited; its own assignment is excluded from the
  // availability check so the technician already on it is not flagged
  // "Unavailable" purely because of this job.
  exclude_job_id?: string
}

export interface JobReport {
  id: string
  tenant_id: string
  job_id: string
  technician_id: string | null
  service_type: string | null
  infestation_level: string | null
  urgency_level: string | null
  affected_areas: string[] | null
  signs_of_presence: string[] | null
  observation: string | null
  treatment: string | null
  actions_performed: string | null
  time_spent: string | null
  products_used: Record<string, unknown>[] | null
  materials_used: Array<{ name: string; quantity: string }> | null
  treated_areas: string[] | null
  closure_notes: string | null
  needs_quote: boolean
  quote_subject: string | null
  quote_description: string | null
  additional_quote_id: string | null
  next_visit_date: string | null
  next_visit_notes: string | null
  recommendations: string[] | null
  checklist: ChecklistItem[] | null
  report_photo_ids: string[] | null
  // Resolved, displayable photo/evidence entries (2.3.7). Each id in
  // report_photo_ids is signed to a short-lived url server-side; PDFs carry an
  // application/pdf mime_type so the UI links instead of rendering an <img>.
  report_photos: Array<{
    id: string
    document_id: string | null
    url: string | null
    file_name: string | null
    mime_type: string | null
    size: number | null
  }> | null
  client_signature_file_id: string | null
  client_signature_url: string | null
  technician_signature_file_id: string | null
  technician_signature_url: string | null
  status: 'draft' | 'completed'
  generated_at: string | null
  sent_to_client: boolean
  sent_at: string | null
  created_at: string
  updated_at: string
}

/** Client reports tab row (2.4.10). */
export interface ClientReport extends JobReport {
  job_reference: string | null
  scheduled_date: string | null
  lead_technician_name: string | null
}

export interface BulkSendClientReportsRequest {
  job_ids?: string[]
  recipient_email?: string
  recipient_emails?: string[]
  message?: string
  search?: string
}

export interface BulkSendClientReportsResult {
  sent: string[]
  failed: Array<{ job_id: string; reason: string }>
}
export interface ChecklistItem {
  key: string
  label: string
  done: boolean
}

export interface RepeatSchedule {
  id: string
  tenant_id: string
  job_id: string | null
  frequency: 'daily' | 'weekly' | 'monthly' | 'custom'
  interval: number
  weekdays: number[] | null
  start_date: string
  end_date: string | null
  occurrences: number | null
}

export interface AgendaSchedule {
  view: 'day' | 'week'
  range: { from: string; to: string }
  technicians: Array<{ technician: Technician; jobs: Job[] }>
}

export type AgendaScheduleJobRequest = {
  scheduled_date?: string
  start_time?: string | null
  end_time?: string | null
  technician_ids: string[]
}

export type AgendaResizeJobRequest = {
  start_time: string
  end_time: string
}

export interface CreateJobRequest {
  client_id: string
  contract_id?: string | null
  quote_id?: string
  original_job_id?: string | null
  priority: JobPriority
  service_type?: string
  intervention_type: InterventionType
  scheduled_date: string
  start_time?: string
  end_time?: string
  country?: string
  city?: string
  postal_code?: string
  address?: string
  address_additional?: string
  access_code?: string
  floor?: string
  access_information?: string
  latitude?: number
  longitude?: number
  note?: string
  payment_option?:
    | 'to_collect'
    | 'quote_on_site'
    | 'no_collection'
    | 'effort_based'
    | null
  amount_to_collect?: number | null
  no_collection_reason?: string | null
  technician_ids?: string[]
  lead_technician_id?: string
}
export type EditJobRequest = Partial<CreateJobRequest>
export interface AssignTechniciansRequest {
  technician_ids: string[]
}
export interface CancelJobRequest {
  cancel_reason: string
}
export interface RescheduleJobRequest {
  callback_date: string
  callback_time?: '00:00' | '08:00' | '14:00'
}
export const JOB_DELETE_SCOPE = {
  this_occurrence: 'this_occurrence',
  all_future_occurrences: 'all_future_occurrences',
} as const
export type DeleteJobScope =
  (typeof JOB_DELETE_SCOPE)[keyof typeof JOB_DELETE_SCOPE]
export interface DeleteJobQuery {
  scope?: DeleteJobScope
}
export interface RepeatScheduleRequest {
  frequency: 'daily' | 'weekly' | 'monthly' | 'custom'
  interval?: number
  weekdays?: number[]
  start_date: string
  end_date?: string
  occurrences?: number
}
export interface JobReportRequest {
  service_type?: string
  infestation_level?: string
  urgency_level?: string
  affected_areas?: string[]
  signs_of_presence?: string[]
  observation?: string
  treatment?: string
  actions_performed?: string
  time_spent?: string
  products_used?: Record<string, unknown>[]
  materials_used?: Array<{ name: string; quantity: string }>
  treated_areas?: string[]
  closure_notes?: string
  needs_quote?: boolean
  quote_subject?: string
  quote_description?: string
  next_visit_date?: string
  next_visit_notes?: string
  recommendations?: string[]
  checklist?: ChecklistItem[]
  report_photo_ids?: string[]
  client_signature_file_id?: string | null
  technician_signature_file_id?: string | null
  status?: 'draft' | 'completed'
}
// Per-service-category job-report option lists (Catalog 2.3.7 / 2.3.9). Served
// by GET /report-catalog; labels arrive already localized to the request's
// Accept-Language, so they are rendered directly (not through i18n keys).
export interface ReportCatalogCategory {
  name: string
  affected_areas: string[]
  signs_of_presence: string[]
  treatment_methods: string[]
  products_used: string[]
  equipment_used: string[]
  recommendations: string[]
}
export interface ReportCatalogResponse {
  categories: ReportCatalogCategory[]
}
export interface SendDocumentRequest {
  recipient_email?: string
  recipient_emails?: string[]
  subject?: string
  message?: string
  include_bank_details?: boolean
  request_signature?: boolean
  allow_online_payment?: boolean
  mode?: 'final' | 'draft'
}
export interface AgendaQuery {
  date_from?: string
  date_to?: string
  technician_id?: string[]
  technician_page?: number
  technician_limit?: number
  view?: 'day' | 'week'
  status?: string
}
export interface JobListQuery extends ListQuery {
  status?: string
  client_id?: string
  exclude_job_id?: string
  technician_id?: string
  date_from?: string
  date_to?: string
}
export interface PdfLink {
  url: string
  filename?: string
  expires_at: string | null
}

// --- Display label maps + agenda technician palette (2.3.1) ---
export const JOB_STATUS_LABELS: Record<JobStatus, string> = {
  to_plan: i18n.t('enums:jobStatus.to_plan'),
  planned: i18n.t('enums:jobStatus.planned'),
  assigned: i18n.t('enums:jobStatus.assigned'),
  in_progress: i18n.t('enums:jobStatus.in_progress'),
  completed: i18n.t('enums:jobStatus.completed'),
  cancelled: i18n.t('enums:jobStatus.cancelled'),
}
export const JOB_PRIORITY_LABELS: Record<JobPriority, string> = {
  low: i18n.t('enums:jobPriority.low'),
  medium: i18n.t('enums:jobPriority.medium'),
  high: i18n.t('enums:jobPriority.high'),
  urgent: i18n.t('enums:jobPriority.urgent'),
}

// Canonical payment-option values + display labels. Use these instead of
// hard-coding the string literals at call sites.
export const PAYMENT_OPTION = {
  to_collect: 'to_collect',
  quote_on_site: 'quote_on_site',
  no_collection: 'no_collection',
  effort_based: 'effort_based',
} as const
export const PAYMENT_OPTION_LABELS: Record<
  Exclude<PaymentOption, null>,
  string
> = {
  to_collect: i18n.t('jobs:payment.options.to_collect'),
  quote_on_site: i18n.t('jobs:payment.options.quote_on_site'),
  no_collection: i18n.t('jobs:payment.options.no_collection'),
  effort_based: i18n.t('jobs:payment.options.effort_based'),
}

// Distinct per-technician colour palette (AC-2.3.1-005/016), aligned to the
// Figma SDS / Ant-Design colour families. Each entry pairs a strong `accent`
// (legend dot, lane header, entry left rail) with a light `tint` used as the
// entry fill (the design's soft filled-block look, not a faint wash). 12 hues
// so adjacent technician lanes stay distinguishable; cycles for growth.
export const TECHNICIAN_COLORS = [
  { accent: '#d4380d', tint: '#ffd8bf' }, // Volcano
  { accent: '#2a61ba', tint: '#e1ebfa' }, // Blue
  { accent: '#008043', tint: '#cff7d3' }, // Green
  { accent: '#531dab', tint: '#efdbff' }, // Purple
  { accent: '#d46b08', tint: '#ffe7ba' }, // Orange
  { accent: '#08979c', tint: '#b5f5ec' }, // Cyan
  { accent: '#c41d7f', tint: '#ffd6e7' }, // Magenta
  { accent: '#7cb305', tint: '#f4ffb8' }, // Lime
  { accent: '#d48806', tint: '#fff1b8' }, // Gold
  { accent: '#1d39c4', tint: '#d6e4ff' }, // Geekblue
  { accent: '#cf1322', tint: '#ffccc7' }, // Red
  { accent: '#006d75', tint: '#87e8de' }, // Teal
] as const

export function technicianColor(index: number): string {
  return TECHNICIAN_COLORS[index % TECHNICIAN_COLORS.length].accent
}

export function technicianTint(index: number): string {
  return TECHNICIAN_COLORS[index % TECHNICIAN_COLORS.length].tint
}
